// Generated by GitHub Copilot - GPT-4.1
import React, { useContext, useEffect, useRef, useState } from "react";
import PubNub from 'pubnub';
import {
    Box,
    Button,
    LinearProgress,
    Typography
} from "@mui/material";
import { useFetch } from "../auth/useFetch";
import ProjectContext from "../../contexts/ProjectContext";
import UserContext from '../../contexts/UserContext';

// Polling configuration
const POLL_INTERVAL_MS = 10000; // Time between polls
const EXTRA_POLL_DURATION_MS = 30000; // Extra polling before stopping

export default function MASVTransferProgress(props) {
    const { apiRef, progressColor, row } = props;
    const fetchWithAuth = useFetch();
    const projectContext = useContext(ProjectContext);
    const { konsolUser } = useContext(UserContext);

    const [polling, setPolling] = useState(false);
    const [progressValue, setProgressValue] = useState(100);

    const graceStart = useRef(null);
    const graceTimeout = useRef(null);
    const pollTimeout = useRef(null);
    const pubnubSubscriptions = useRef({});


    useEffect(() => {
        if (polling) return;
        if (["new", "pending", 'finalizing'].indexOf(row?.transfer_status) !== -1) {
            startPolling();
        }
    }, [row?.transfer_status]);

    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, []);

    const bytesToHumanReadable = (bytes) => {
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
        if (bytes === 0) return '0 Bytes';
        const k = 1024
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
        return `${size} ${sizes[i]}`;
    }

    const fetchKonsolPackageDetails = async () => {
        try {
            const res = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/masv?package_id=${row?.package_id}`, {
                headers: {
                    'X-Project-ID': projectContext?.project_db
                }
            });
            if (!res.ok) throw new Error('Failed to fetch package details');
            return await res.json();
        } catch (error) {
            throw new Error('Failed to fetch package details');
        }
    };

    const fetchMASVTransferDetails = async (accessToken) => {
        try {
            const res = await fetch(`https://api.massive.app/v1/packages/${row?.package_id}/transfer`, {
                headers: {
                    "X-Package-Token": accessToken
                }
            });
            if (!res.ok) throw new Error('Failed to fetch transfer details');
            return await res.json();
        } catch (error) {
            throw new Error('Failed to fetch transfer details');
        }
    };

    function getLatestCompletion(arr) {
        const dates = arr.map(item => {
            let completed_at = new Date(item?.updated_at)
            if(isNaN(completed_at)){
                completed_at = new Date()
            }
            return completed_at
        })
            .filter(date => date !== null);
        if (dates.length === 0) return null;
        return new Date(Math.max(...dates));
    }

    function getOverallState(arr) {
        if (arr.every(item => item?.state === 'completed')) {
            return 'completed';
        }
        if (arr.some(item => item?.state === 'cancelled')) {
            return 'cancelled';
        }
        // Ignore in all other cases
        return 'error';
    }

    const pollMASV = async (accessToken) => {
        let masvTransferDetails = [];
        try {
            masvTransferDetails = await fetchMASVTransferDetails(accessToken);
        } catch (error) {
            const updatedRow = {...row, transfer_status: 'error'};
            apiRef.current?.updateRows([{ id: row?.id, transfer_status: 'error'}]);
            updateTransferDetails(updatedRow);
            stopPolling();
            return;
        }

        if (Array.isArray(masvTransferDetails)) {
            masvTransferDetails.forEach(item => {
                const progressChannel = item?.progress_channel;
                const channelName = progressChannel?.auth?.channel_name;
                if (progressChannel && channelName) {
                    subscribeToPubNub(progressChannel, channelName);
                }
            });
        }

        const hasNewOrPending = masvTransferDetails.some(
            t => t?.state === "new" || t?.state === "pending"
        );
        const transferStatus = {}

        if (hasNewOrPending) {
            // If there are new or pending transfers, continue polling
            graceStart.current = null;
            pollTimeout.current = setTimeout(() => {
                pollMASV(accessToken);
            }, POLL_INTERVAL_MS);
        } else {
            // All items are not new/pending, start or continue grace timer
            transferStatus['transfer_status'] = 'finalizing'
            if (!graceStart.current) {
                graceStart.current = Date.now();
            }
            if (Date.now() - graceStart.current < EXTRA_POLL_DURATION_MS) {
                graceTimeout.current = setTimeout(() => {
                    pollMASV(accessToken);
                }, POLL_INTERVAL_MS);
            } else {
                transferStatus['transfer_status'] = getOverallState(masvTransferDetails);
                transferStatus['completed_at'] = getLatestCompletion(masvTransferDetails);
                updateTransferDetails(transferStatus);
                stopPolling();
            }
            apiRef.current?.updateRows([{ id: row?.id, ...transferStatus}]);
        }
    }

    const startPolling = async () => {
        try {
            setPolling(true);
            const konsolPackage = await fetchKonsolPackageDetails();
            pollMASV(konsolPackage?.access_token);
        } catch {
            const updatedRow = {...row, transfer_status: 'error'};
            apiRef.current?.updateRows([{ id: row?.id, transfer_status: 'error'}]);
            updateTransferDetails(updatedRow);
            stopPolling();
            return;
        }
    }

    const stopPolling = () => {
        clearTimeout(pollTimeout.current);
        clearTimeout(graceTimeout.current);
        setPolling(false);
        setProgressValue(100);

        pollTimeout.current = null;
        graceTimeout.current = null;
        graceStart.current = null;

        unsubscribeAllPubNub();
    }

const subscribeToPubNub = (progressChannel, channelName) => {
    if (pubnubSubscriptions.current[channelName]) return; // Already subscribed

    const pubnubConfiguration = {
        publishKey: progressChannel?.auth?.publish_key,
        subscribeKey: progressChannel?.auth?.subscribe_key,
        authKey: progressChannel?.auth?.token,
        userId: `konsol-${projectContext?.project_db}-${konsolUser?.sub}-${Math.random().toString(36).substring(2, 15)}`,
    };
    const pubnub = new PubNub(pubnubConfiguration);
    const channel = pubnub.channel(progressChannel?.auth?.channel_name);
    const subscription = channel.subscription();

    subscription.onMessage = (message) => {
        const total = message?.message?.total ?? 1;
        const completed = message?.message?.completed ?? 0;
        setProgressValue(Math.floor((completed / total) * 100));
    };

    subscription.subscribe();
    pubnubSubscriptions.current[channelName] = { pubnub, subscription };
    setProgressValue(0);
};

    const unsubscribeAllPubNub = () => {
        Object.values(pubnubSubscriptions.current).forEach(({ pubnub, subscription }) => {
            try {
                subscription.unsubscribe();
                pubnub.unsubscribeAll();
            } catch (e) {}
        });
        pubnubSubscriptions.current = {};
    };

    const updateTransferDetails = async (transferStatus) => {
        try {
            await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/konsol/masv/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Project-ID': projectContext.project_db,
                },
                body: JSON.stringify({
                    package_id: row.package_id,
                    ...transferStatus
                })
            });
        }
        catch (error) {
            console.error("Error updating transfer state:", error);
        }
    }

    return (
        <Box display="flex" alignItems="center" position="relative" minHeight={32}>
            <Box flex={1} display="flex" justifyContent="center" alignItems="center">
                <Box minWidth="180px" maxWidth="180px" width="100%">
                    <LinearProgress
                        sx={{
                            borderRadius: 1,
                            [` .MuiLinearProgress-bar`]: {
                                borderRadius: 1,
                                backgroundColor: progressColor ?? 'rgb(158, 158, 158)',
                            },
                            [`&.MuiLinearProgress-colorPrimary`]: {
                                backgroundColor: 'rgb(75, 75 ,75)'
                            },
                        }}
                        variant={row?.transfer_status === 'finalizing' ? 'indeterminate' : 'determinate'}
                        value={progressValue}
                    />
                </Box>
            </Box>
            <Typography
                sx={{
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    fontSize: "10px",
                    color: "rgb(255, 255, 255)",
                }}
            >
                {row?.transfer_status === "pending" ? `${progressValue}%` : ""}
            </Typography>
        </Box>
    )
}